import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';

import { activityNumbersToActivities } from 'app/shared/features/activity-selector/utils';
import { selectActivityListIfLoaded } from 'app/state/activities/activity.selectors';
import { selectEmployeeList } from 'app/state/employee/employee.selectors';

import { userSessionFeatureStoreKey, UserSessionState } from './user-session.reducer';

export const selectUserSessionState = createFeatureSelector<UserSessionState>(userSessionFeatureStoreKey);

export const selectUserSession = createSelector(selectUserSessionState, (state) => state.userSession);
export const selectPersonalUserPreferences = createSelector(
    selectUserSession,
    (userSession) => userSession?.data,
);
export const selectUserRecentActivityNumbers = createSelector(
    selectPersonalUserPreferences,
    (personalUserPreferences) => personalUserPreferences?.lastActivityNumbers ?? [],
);

export const selectUserRecentActivityNumberIfLoaded = createSelector(
    selectPersonalUserPreferences,
    (personalUserPreferences) => personalUserPreferences?.lastActivityNumbers[0] ?? undefined,
);

export const selectUserRecentActivities = (store: Observable<object>) =>
    combineLatest([
        store.pipe(select(selectUserRecentActivityNumbers)),
        store.pipe(selectActivityListIfLoaded),
    ]).pipe(
        map(([recentActivityNumbers, activities]) =>
            activityNumbersToActivities({ activityNumbers: recentActivityNumbers, activities }),
        ),
    );

export const selectUserSessionHistory = createSelector(selectUserSessionState, (state) => state.history);

export const selectLatestSupervisorInHistoryIfDefined = createSelector(
    selectUserSessionHistory,
    (userSessionHistory) => {
        return userSessionHistory.find((userSession) => userSession.userInfo?.roles.includes('supervisor'));
    },
);

export const selectUserSessionFromHistory = (employeeNumber: number) =>
    createSelector(selectUserSessionHistory, (history) =>
        history.find((s) => s.credentials.employeeNumber === employeeNumber),
    );

export const selectUserSessionCredentials = createSelector(selectUserSession, (state) => state?.credentials);

export const selectUserSessionEmployeeNumber = createSelector(
    selectUserSessionCredentials,
    (credentials) => credentials?.employeeNumber,
);

export const selectIsAuthenticated = createSelector(
    selectUserSessionCredentials,
    (credentials): boolean => !!credentials?.employeeNumber && !!credentials.pin,
);

export const selectUserSessionEmployee = createSelector(
    selectUserSessionEmployeeNumber,
    selectEmployeeList,
    (employeeNumber, employeeList) => {
        if (!employeeNumber) return undefined;
        const employee = employeeList.find((e) => e.number === employeeNumber);
        return { employeeNumber: employeeNumber, employee };
    },
);
